import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gqlErrors';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	ContactDecisionMakerDraftSource,
	ContactDecisionMakerReasonId,
} from '#src/app/shared/utils/contactDecisionMaker';
import { seedDraft } from '#src/app/shared/utils/contactDecisionMaker';

import type {
	DecisionMakerMessageReason,
	GenerateDecisionMakerMessageDraftMutation,
	GenerateDecisionMakerMessageDraftMutationVariables,
} from './api.graphql';
import { GenerateDecisionMakerMessageDraft } from './api.graphql';

const REASON_BY_ID: Record<ContactDecisionMakerReasonId, DecisionMakerMessageReason> = {
	affects: 'AFFECTS',
	care: 'CARE',
	community: 'COMMUNITY',
	right: 'RIGHT',
	own: 'OWN',
};

export type DraftResult = {
	draft: string;
	source: ContactDecisionMakerDraftSource;
};

type Params = {
	petitionId: string;
	decisionMakerId: string;
	decisionMakerName: string;
	reasonId: ContactDecisionMakerReasonId;
	story: string;
};

/**
 * The client-side seed the "I'll write it myself" path uses: the supporter's story, addressed to the
 * decision maker. Every failure path hands this back so the supporter still reaches the review screen.
 */
export function seededFallbackDraft(
	{ decisionMakerName, story }: Pick<Params, 'decisionMakerName' | 'story'>,
	i18n: UtilityContext['i18n'],
): DraftResult {
	return { draft: seedDraft(i18n.translate, decisionMakerName, story), source: 'fallback' };
}

/**
 * Asks the middle-end to draft the message, and resolves with something sendable for every failure
 * except a PerimeterX challenge.
 *
 * The middle-end already falls back to a deterministic template when the prompt executor fails, so a
 * `GenerateDecisionMakerMessageDraftSuccess` with `source: 'FALLBACK'` is not a failure at all. Actual
 * failures — the feature being disabled, the request never landing, or a bug — get the same
 * client-side seed the "I'll write it myself" path uses, because the supporter has already written
 * their story by this point and dropping them into an error screen would throw that away.
 *
 * A `GQLResponsePerimeterXChallengeError` is rethrown instead, so the calling hook can open the
 * captcha and retry before it settles for the seed.
 */
export async function generateDecisionMakerMessageDraft(
	{ petitionId, decisionMakerId, decisionMakerName, reasonId, story }: Params,
	{ gql: { fetch }, errorReporter, i18n }: UtilityContext,
): Promise<DraftResult> {
	const fallback = seededFallbackDraft({ decisionMakerName, story }, i18n);

	try {
		const { data, requestId } = await fetch<
			GenerateDecisionMakerMessageDraftMutation,
			GenerateDecisionMakerMessageDraftMutationVariables
		>({
			query: GenerateDecisionMakerMessageDraft,
			variables: { input: { petitionId, decisionMakerId, reason: REASON_BY_ID[reasonId], story } },
			// Must match the mutation's @restrictPath on the middle-end resolver.
			path: '/generate-decision-maker-message-draft',
			rejectOnError: true,
			// The mutation has @restrictCount, so it must not share a request with anything else.
			batched: false,
		});

		const result = data.generateDecisionMakerMessageDraft;

		if (result.__typename === 'GenerateDecisionMakerMessageDraftError') {
			void errorReporter.report({
				error: new Error('generateDecisionMakerMessageDraft failed'),
				context: { requestId },
				params: { reason: result.reason },
			});
			return fallback;
		}

		return { draft: result.draft, source: result.source === 'LLM' ? 'llm' : 'fallback' };
	} catch (error) {
		if (error instanceof GQLResponsePerimeterXChallengeError) throw error;
		void errorReporter.report({ error: error instanceof Error ? error : new Error(String(error)) });
		return fallback;
	}
}
